import { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { QRCodeCanvas } from "qrcode.react";
import { Assets, FontFamily, PrimaryButton } from "utils/imports.utils";
import { ButtonSize, ButtonTypes } from "utils/interface.utils";
import { composeTargetImageWithQr, convertImageUrlToFile, getProjectImagesFolderName, getTargetImageFileName, loadImageFromUrl, saveBytesAs, toastifyError, toastifySuccess } from "utils/functions.utils";
import { TARGET_QR_ERROR_CORRECTION_LEVEL, TARGET_QR_QUIET_ZONE_MODULES, TARGET_QR_SOURCE_SIZE_PX, ZIP_MIME_TYPE } from "utils/constant.utils";
import { createZip } from "adapters/zip/zip.adapter";
import { STRINGS } from "constants/strings.constant";
import { DownloadProjectImagesProps, TargetImageSource, ZipEntry } from "interfaces/target_qr_download.interface";
import "./download_project_images.component.scss";

const DownloadProjectImages = (props: DownloadProjectImagesProps) => {
    const { isEnabled, projectName, targetImages, arLink, className, headless, isHintHidden, onFinished } = props;
    const [state, setState] = useState({ isPreparing: false });
    const hasAutoStartedRef = useRef(false);
    // Never shown: the QR code drawn onto every downloaded image is copied from this canvas.
    const qrCanvasRef = useRef<HTMLCanvasElement>(null);

    const handleDownload = async () => {
        const qrCanvas = qrCanvasRef.current;
        if (!isEnabled || state.isPreparing || !qrCanvas) {
            // A headless run has no button to try again from: hand control back so the caller can reset.
            if (headless) onFinished?.();
            return;
        }
        setState((prev) => ({ ...prev, isPreparing: true }));
        // The logo is branding, not content: if it cannot be loaded, the images still download.
        let logo: TargetImageSource | undefined;
        try {
            logo = await loadImageFromUrl(Assets.qr_logo);
        } catch (error) {
            console.error('Failed to load the QR code logo; downloading without it:', error);
        }
        try {
            const folderName = getProjectImagesFolderName(projectName);
            const entries: ZipEntry[] = [];
            // One at a time: a full-size canvas per image is held in memory while it is drawn.
            for (let index = 0; index < targetImages.length; index++) {
                const file = await convertImageUrlToFile(targetImages[index]);
                const data = await composeTargetImageWithQr(file, qrCanvas, logo);
                entries.push({ path: `${folderName}/${getTargetImageFileName(index, file.type)}`, data });
            }
            saveBytesAs(createZip(entries), ZIP_MIME_TYPE, `${folderName}.zip`);
            toastifySuccess(STRINGS.preview.downloadImages.success);
        } catch (error) {
            console.error('Failed to download the project images:', error);
            toastifyError(STRINGS.preview.downloadImages.failed);
        } finally {
            setState((prev) => ({ ...prev, isPreparing: false }));
            onFinished?.();
        }
    }

    useEffect(() => {
        // The ref keeps a re-run of the effect (StrictMode) from starting a second download.
        if (!headless || hasAutoStartedRef.current) return;
        hasAutoStartedRef.current = true;
        handleDownload();
    }, []);

    return (
        <div className={clsx("download_project_images", className)}>
            {!headless && (
                <div className="download_project_images__button">
                    <PrimaryButton
                        text={STRINGS.preview.downloadImages.button}
                        left_icon={Assets.download}
                        type={ButtonTypes.SECONDARY}
                        size={ButtonSize.MEDIUM}
                        fontFamily={FontFamily.semiBold}
                        iconSize={{ width: 18, height: 18 }}
                        onClick={handleDownload}
                        activity={state.isPreparing}
                        disabled={!isEnabled || state.isPreparing}
                    />
                </div>
            )}
            {!headless && !isHintHidden && !isEnabled && (
                <p className="download_project_images__hint">{STRINGS.preview.downloadImages.lockedHint}</p>
            )}
            <div className="download_project_images__qr_source" aria-hidden="true">
                <QRCodeCanvas
                    ref={qrCanvasRef}
                    value={arLink}
                    size={TARGET_QR_SOURCE_SIZE_PX}
                    marginSize={TARGET_QR_QUIET_ZONE_MODULES}
                    level={TARGET_QR_ERROR_CORRECTION_LEVEL}
                />
            </div>
        </div>
    )
}

export default DownloadProjectImages;
